<template>
    <div class="account-pool-management">
        <!-- 主内容区域 -->
        <div class="main-content">
            <!-- 标签页 -->
            <div class="tab-header">
                <el-tabs v-model="activeTab" @tab-change="handleTabChange">
                    <el-tab-pane label="自媒体账号" name="media">
                        <template #label>
                            <span class="tab-label">
                                <el-icon>
                                    <User />
                                </el-icon>
                                自媒体账号
                                <el-badge :value="stats.media_count" :max="99" class="tab-badge" />
                            </span>
                        </template>
                    </el-tab-pane>
                    <el-tab-pane label="智能体网站" name="agent">
                        <template #label>
                            <span class="tab-label">
                                <el-icon>
                                    <Monitor />
                                </el-icon>
                                智能体网站
                                <el-badge :value="stats.agent_count" :max="99" class="tab-badge" />
                            </span>
                        </template>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <!-- 搜索和操作区域 -->
            <div class="operation-bar">
                <div class="search-section">
                    <el-input v-model="searchForm.name" :placeholder="searchPlaceholder" clearable size="large"
                        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>

                    <el-button type="primary" size="large" @click="handleSearch">
                        <el-icon>
                            <Search />
                        </el-icon>
                        搜索
                    </el-button>

                    <el-button type="info" size="large" @click="resetSearch">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        重置
                    </el-button>
                </div>

                <div class="stats-info">
                    <!-- 自媒体账号页面的统计 -->
                    <template v-if="activeTab === 'media'">
                        <div class="stat-item">
                            <span class="label">总数量：</span>
                            <span class="value">{{ stats.media_count }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="label">站点端：</span>
                            <span class="value">{{ currentStats.siteCount }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="label">用户端：</span>
                            <span class="value">{{ currentStats.userCount }}</span>
                        </div>
                    </template>

                    <!-- 智能体网站页面的统计 -->
                    <template v-if="activeTab === 'agent'">
                        <div class="stat-item">
                            <span class="label">总数量：</span>
                            <span class="value">{{ stats.agent_count }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="label">站点端：</span>
                            <span class="value">{{ currentStats.siteCount }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="label">用户端：</span>
                            <span class="value">{{ currentStats.userCount }}</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- 自媒体账号列表 -->
            <div v-if="activeTab === 'media'" class="tab-content">
                <el-table :data="mediaList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
                    class="account-table">
                    <el-table-column prop="name" label="账号名称" width="180" />

                    <el-table-column label="平台类型" width="120">
                        <template #default="{ row }">
                            <el-tag :type="getMediaPlatformTagType(row.field_type)">
                                {{ row.platform_text }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="账号来源" width="100">
                        <template #default="{ row }">
                            <el-tag :type="row.account_source === '站点端' ? 'success' : 'warning'">
                                {{ row.account_source }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="所属用户" width="150">
                        <template #default="{ row }">
                            <div class="owner-info">
                                <div class="owner-name">{{ row.owner_name || '未知' }}</div>
                                <div v-if="row.owner_phone" class="owner-phone">{{ row.owner_phone }}</div>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="授权方式" width="120">
                        <template #default="{ row }">
                            <template v-if="row.auth_method === 1 || row.auth_method === 2">
                                <el-tag :type="row.auth_method === 1 ? 'success' : 'warning'">
                                    {{ getAuthMethodText(row.auth_method) }}
                                </el-tag>
                            </template>
                            <span v-else class="empty-value">-</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="AppID/Secret" width="200">
                        <template #default="{ row }">
                            <div v-if="row.auth_method === 1">
                                <div>AppID: {{ row.ghz_appid || '未设置' }}</div>
                                <div>Secret: {{ row.ghz_secret ? '******' : '未设置' }}</div>
                            </div>
                            <div v-else class="empty-value">-</div>
                        </template>
                    </el-table-column>

                    <el-table-column label="RPA授权状态" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.cookies ? 'success' : 'danger'">
                                {{ row.cookies ? '已授权' : '未授权' }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="IP+端口" width="150">
                        <template #default="{ row }">
                            <span v-if="row.ip_port">{{ row.ip_port }}</span>
                            <span v-else class="empty-value">未设置</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="账号密码" width="180">
                        <template #default="{ row }">
                            <span v-if="row.ip_password" class="password-preview">
                                {{ row.ip_password }}
                            </span>
                            <span v-else class="empty-value">未设置</span>
                        </template>
                    </el-table-column>

                    <el-table-column prop="notes" label="备注信息" width="150" show-overflow-tooltip />

                    <el-table-column prop="update_time" label="更新时间" width="180" />
                    <el-table-column prop="create_time" label="创建时间" width="180" />

                    <el-table-column label="操作" width="120" fixed="right">
                        <template #default="scope">
                            <el-button type="primary" size="small" @click="viewAccountDetail(scope.row, 'media')">
                                <el-icon>
                                    <View />
                                </el-icon>
                                查看
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页组件 -->
                <div class="pagination-container">
                    <el-pagination v-model:current-page="mediaPagination.page"
                        v-model:page-size="mediaPagination.pageSize" :total="mediaPagination.total"
                        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
                        @size-change="fetchMediaList" @current-change="fetchMediaList" />
                </div>
            </div>

            <!-- 智能体网站列表 -->
            <div v-if="activeTab === 'agent'" class="tab-content">
                <el-table :data="agentList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
                    class="account-table">
                    <el-table-column prop="name" label="网站名称" width="180" />

                    <el-table-column label="平台类型" width="120">
                        <template #default="{ row }">
                            <el-tag type="primary">
                                {{ row.platform_text }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="账号来源" width="100">
                        <template #default="{ row }">
                            <el-tag :type="row.account_source === '站点端' ? 'success' : 'warning'">
                                {{ row.account_source }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="所属用户" width="150">
                        <template #default="{ row }">
                            <div class="owner-info">
                                <div class="owner-name">{{ row.owner_name || '未知' }}</div>
                                <div v-if="row.owner_phone" class="owner-phone">{{ row.owner_phone }}</div>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="认证方式" width="120">
                        <template #default="{ row }">
                            <el-tag :type="getAgentAuthTypeTag(row.auth_type)">
                                {{ getAgentAuthTypeText(row.auth_type) }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="API密钥" width="200">
                        <template #default="{ row }">
                            <div v-if="row.ghz_appid">
                                {{ row.ghz_appid ? '******' : '未设置' }}
                            </div>
                            <div v-else class="empty-value">-</div>
                        </template>
                    </el-table-column>

                    <el-table-column label="状态" width="80">
                        <template #default="{ row }">
                            <el-tag :type="row.status === 1 ? 'success' : 'info'">
                                {{ row.status === 1 ? '启用' : '禁用' }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="同步次数" width="100">
                        <template #default="{ row }">
                            {{ row.sync_count || 0 }}
                        </template>
                    </el-table-column>

                    <el-table-column label="最后同步" width="180">
                        <template #default="{ row }">
                            {{ row.last_sync_time || '从未同步' }}
                        </template>
                    </el-table-column>

                    <el-table-column prop="update_time" label="更新时间" width="180" />
                    <el-table-column prop="create_time" label="创建时间" width="180" />

                    <el-table-column label="操作" width="120" fixed="right">
                        <template #default="scope">
                            <el-button type="primary" size="small" @click="viewAccountDetail(scope.row, 'agent')">
                                <el-icon>
                                    <View />
                                </el-icon>
                                查看
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页组件 -->
                <div class="pagination-container">
                    <el-pagination v-model:current-page="agentPagination.page"
                        v-model:page-size="agentPagination.pageSize" :total="agentPagination.total"
                        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
                        @size-change="fetchAgentList" @current-change="fetchAgentList" />
                </div>
            </div>
        </div>

        <!-- 账号详情对话框 -->
        <el-dialog v-model="detailDialogVisible" :title="detailTitle" width="700px" :close-on-click-modal="false"
            class="account-detail-dialog">
            <div class="account-detail" v-if="currentAccount">
                <el-descriptions :column="2" border>
                    <el-descriptions-item label="类型">
                        <el-tag :type="currentAccountType === 'media' ? 'primary' : 'success'">
                            {{ currentAccountType === 'media' ? '自媒体账号' : '智能体网站' }}
                        </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="名称">{{ currentAccount.name }}</el-descriptions-item>

                    <el-descriptions-item label="平台类型">
                        <el-tag
                            :type="currentAccountType === 'media' ? getMediaPlatformTagType(currentAccount.field_type) : 'primary'">
                            {{ currentAccount.platform_text }}
                        </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="账号来源">
                        <el-tag :type="currentAccount.account_source === '站点端' ? 'success' : 'warning'">
                            {{ currentAccount.account_source }}
                        </el-tag>
                    </el-descriptions-item>

                    <el-descriptions-item label="所属用户">{{ currentAccount.owner_name || '未知' }}</el-descriptions-item>
                    <el-descriptions-item label="用户手机" v-if="currentAccount.owner_phone">
                        {{ currentAccount.owner_phone }}
                    </el-descriptions-item>

                    <!-- 自媒体详情 -->
                    <template v-if="currentAccountType === 'media'">
                        <el-descriptions-item label="授权方式">
                            {{ getAuthMethodText(currentAccount.auth_method) }}
                        </el-descriptions-item>
                        <el-descriptions-item label="RPA授权状态">
                            <el-tag :type="currentAccount.cookies ? 'success' : 'danger'">
                                {{ currentAccount.cookies ? '已授权' : '未授权' }}
                            </el-tag>
                        </el-descriptions-item>

                        <el-descriptions-item label="AppID">{{ currentAccount.ghz_appid || '未设置'
                        }}</el-descriptions-item>
                        <el-descriptions-item label="Secret">
                            {{ currentAccount.ghz_secret ? '******' : '未设置' }}
                        </el-descriptions-item>

                        <el-descriptions-item label="IP+端口">{{ currentAccount.ip_port || '未设置' }}</el-descriptions-item>
                        <el-descriptions-item label="账号密码">{{ currentAccount.ip_password || '未设置'
                        }}</el-descriptions-item>
                    </template>

                    <!-- 智能体详情 -->
                    <template v-if="currentAccountType === 'agent'">
                        <el-descriptions-item label="认证方式">
                            {{ getAgentAuthTypeText(currentAccount.auth_type) }}
                        </el-descriptions-item>
                        <el-descriptions-item label="状态">
                            <el-tag :type="currentAccount.status === 1 ? 'success' : 'info'">
                                {{ currentAccount.status === 1 ? '启用' : '禁用' }}
                            </el-tag>
                        </el-descriptions-item>

                        <el-descriptions-item label="API密钥">
                            {{ currentAccount.ghz_appid ? '******' : '未设置' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="同步次数">{{ currentAccount.sync_count || 0 }}</el-descriptions-item>

                        <el-descriptions-item label="最后同步时间">
                            {{ currentAccount.last_sync_time || '从未同步' }}
                        </el-descriptions-item>
                    </template>

                    <el-descriptions-item label="备注信息" span="2">{{ currentAccount.notes || '无' }}</el-descriptions-item>

                    <el-descriptions-item label="创建时间">{{ currentAccount.create_time }}</el-descriptions-item>
                    <el-descriptions-item label="更新时间">{{ currentAccount.update_time }}</el-descriptions-item>
                </el-descriptions>
            </div>

            <template #footer>
                <el-button @click="detailDialogVisible = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import {
    Search, Refresh, View, User, Monitor
} from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
    name: 'AccountPoolManagement',
    components: { Search, Refresh, View, User, Monitor },
    data() {
        return {
            loading: false,
            detailDialogVisible: false,
            currentAccount: null,
            currentAccountType: 'media',
            activeTab: 'media',

            // 搜索表单
            searchForm: {
                name: ''
            },

            // 自媒体账号列表
            mediaList: [],
            mediaPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 智能体网站列表
            agentList: [],
            agentPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 统计信息
            stats: {
                media_count: 0,
                agent_count: 0,
                total_count: 0
            },

            currentStats: {
                siteCount: 0,
                userCount: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('store_user_info') || '{}')
        },
        userId() {
            return this.userInfo.id
        },
        storeId() {
            return this.userInfo.store_id
        },
        detailTitle() {
            return this.currentAccountType === 'media' ? '自媒体账号详情' : '智能体网站详情'
        },
        searchPlaceholder() {
            return this.activeTab === 'media' ? '请输入账号名称' : '请输入网站名称'
        }
    },
    mounted() {
        this.fetchStats()
        this.fetchMediaList()
    },
    methods: {
        // 获取统计信息
        async fetchStats() {
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/store/api/account_pool_management.php`,
                    {
                        action: 'stats',
                        store_id: this.storeId,
                        user_id: this.userId
                    }
                )

                if (data) {
                    this.stats = data
                }
            } catch (error) {
                console.error('获取统计信息失败:', error)
            }
        },

        // 获取自媒体账号列表
        async fetchMediaList() {
            this.loading = true
            try {
                const params = {
                    action: this.searchForm.name ? 'search_media' : 'list_media',
                    store_id: this.storeId,
                    user_id: this.userId,
                    page: this.mediaPagination.page,
                    page_size: this.mediaPagination.pageSize
                }

                if (this.searchForm.name) {
                    params.name = this.searchForm.name
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/store/api/account_pool_management.php`,
                    params
                )

                if (data.data) {
                    this.mediaList = data.data
                    this.mediaPagination.total = data.pagination?.total || data.data.length
                    this.calculateCurrentStats(this.mediaList)

                    // 如果当前是自媒体标签页，更新总数量显示
                    if (this.activeTab === 'media') {
                        this.fetchStats() // 重新获取最新统计
                    }
                }
            } catch (error) {
                ElMessage.error('获取自媒体账号列表失败')
                console.error('获取自媒体账号列表错误:', error)
            } finally {
                this.loading = false
            }
        },


        // 获取智能体网站列表
        async fetchAgentList() {
            this.loading = true
            try {
                const params = {
                    action: this.searchForm.name ? 'search_agent' : 'list_agent',
                    store_id: this.storeId,
                    user_id: this.userId,
                    page: this.agentPagination.page,
                    page_size: this.agentPagination.pageSize
                }

                if (this.searchForm.name) {
                    params.name = this.searchForm.name
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/store/api/account_pool_management.php`,
                    params
                )

                if (data.data) {
                    this.agentList = data.data
                    this.agentPagination.total = data.pagination?.total || data.data.length
                    this.calculateCurrentStats(this.agentList)

                    // 如果当前是智能体标签页，更新总数量显示
                    if (this.activeTab === 'agent') {
                        this.fetchStats() // 重新获取最新统计
                    }
                }
            } catch (error) {
                ElMessage.error('获取智能体网站列表失败')
                console.error('获取智能体网站列表错误:', error)
            } finally {
                this.loading = false
            }
        },

        // 计算当前标签页的统计信息
        calculateCurrentStats(accounts) {
            this.currentStats = {
                siteCount: accounts.filter(account => account.account_source === '站点端').length,
                userCount: accounts.filter(account => account.account_source === '用户端').length
            }
        },

        // 获取自媒体平台标签类型
        getMediaPlatformTagType(type) {
            const typeMap = {
                1: 'danger',    // 抖音 - 红色
                2: 'warning',   // 快手 - 橙色
                3: 'danger',    // 小红书 - 红色
                4: 'info',      // B站 - 青色
                5: 'success',   // 视频号 - 绿色
                6: 'success',   // 微信公众号 - 绿色
                7: 'danger',    // 今日头条 - 默认
                8: 'warning',   // 百家号 - 橙色
                9: 'info',      // 知乎 - 青色
                10: 'success'   // 百度文库 - 绿色
            }
            return typeMap[type] || 'default'
        },

        // 获取智能体认证方式标签类型
        getAgentAuthTypeTag(authType) {
            const typeMap = {
                'api_key': 'success',
                'cookie': 'warning',
                'signature': 'primary'
            }
            return typeMap[authType] || 'default'
        },

        // 获取智能体认证方式文本
        getAgentAuthTypeText(authType) {
            const typeMap = {
                'api_key': 'API密钥',
                'cookie': 'Cookie认证',
                'signature': '签名认证'
            }
            return typeMap[authType] || authType
        },

        // 获取授权方式文本
        getAuthMethodText(method) {
            const methods = {
                1: '密钥授权',
                2: '开放平台'
            };
            return methods[method] || '-';
        },

        // 处理搜索
        handleSearch() {
            if (this.activeTab === 'media') {
                this.mediaPagination.page = 1
                this.fetchMediaList()
            } else {
                this.agentPagination.page = 1
                this.fetchAgentList()
            }
        },

        // 重置搜索
        resetSearch() {
            this.searchForm.name = ''
            if (this.activeTab === 'media') {
                this.mediaPagination.page = 1
                this.fetchMediaList()
            } else {
                this.agentPagination.page = 1
                this.fetchAgentList()
            }
        },

        // 标签页切换
        handleTabChange(tabName) {
            this.searchForm.name = ''
            if (tabName === 'media' && this.mediaList.length === 0) {
                this.fetchMediaList()
            } else if (tabName === 'agent' && this.agentList.length === 0) {
                this.fetchAgentList()
            } else {
                // 切换时重新计算当前列表的统计
                const currentList = tabName === 'media' ? this.mediaList : this.agentList
                this.calculateCurrentStats(currentList)
            }
        },

        // 查看账号详情
        viewAccountDetail(account, type) {
            this.currentAccount = account
            this.currentAccountType = type
            this.detailDialogVisible = true
        }
    }
}
</script>

<style scoped lang="scss">
.account-pool-management {
    .main-content {
        background: hsla(0, 0%, 100%, .1);
        border-radius: 16px;
        padding: 24px;
        backdrop-filter: blur(10px);
    }

    .tab-header {
        margin-bottom: 20px;

        :deep(.el-tabs) {
            .el-tabs__header {
                margin-bottom: 0;
            }

            .el-tabs__nav-wrap::after {
                background-color: rgba(255, 112, 72, 0.3);
            }

            .el-tabs__active-bar {
                background-color: #ff7048;
            }

            .el-tabs__item {
                color: #e0e6ff;

                &.is-active {
                    color: #ff7048;
                }

                &:hover {
                    color: #ff7048;
                }
            }
        }
    }

    .tab-label {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .tab-badge {
        :deep(.el-badge__content) {
            background-color: #ff7048;
        }
    }

    .operation-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding: 16px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
    }

    .search-section {
        display: flex;
        align-items: center;
    }

    .stats-info {
        display: flex;
        gap: 20px;
        background: rgba(240, 247, 255, 0.8);
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
    }

    .stat-item {
        display: flex;
        align-items: center;

        .label {
            color: #606266;
            font-weight: 500;
        }

        .value {
            font-weight: 600;
            color: #409eff;
        }
    }

    .account-table {
        border: 1px solid rgba(255, 112, 72, 0.3);
        border-radius: 8px;
        overflow: hidden;

        :deep(.el-table) {
            background: transparent;

            th {
                background: rgba(255, 112, 72, 0.1) !important;
                color: #e0e6ff !important;
                border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
            }

            tr {
                background: rgba(255, 255, 255, 0.05);

                &:hover {
                    background: rgba(255, 255, 255, 0.1) !important;
                }
            }

            td {
                color: #e0e6ff !important;
                border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
            }
        }
    }

    .owner-info {
        .owner-name {
            color: #e0e6ff;
            font-weight: 500;
        }

        .owner-phone {
            color: #a8b1ff;
            font-size: 12px;
            margin-top: 4px;
        }
    }

    .empty-value {
        color: #909399;
        font-style: italic;
    }

    .password-preview {
        font-family: monospace;
        background-color: rgba(245, 247, 250, 0.8);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 13px;
    }

    .pagination-container {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;
    }

    .account-detail {
        :deep(.el-descriptions) {
            background: transparent;

            .el-descriptions__header {
                display: none;
            }

            .el-descriptions__body {
                background: rgba(255, 255, 255, 0.05);
                border: 1px solid rgba(255, 112, 72, 0.3);
                border-radius: 8px;
            }

            .el-descriptions__table {
                background: transparent;

                tbody tr {
                    background: transparent;

                    &:nth-child(2n) {
                        background: rgba(255, 255, 255, 0.02);
                    }
                }

                .el-descriptions__label {
                    color: #e0e6ff !important;
                    font-weight: 500;
                    width: 100px;
                    background: rgba(255, 112, 72, 0.1) !important;
                    border-right: 1px solid rgba(255, 112, 72, 0.2);
                }

                .el-descriptions__content {
                    color: #e0e6ff !important;
                    background: transparent !important;
                    border-right: 1px solid rgba(255, 112, 72, 0.1);
                }

                .el-descriptions__cell {
                    border-bottom: 1px solid rgba(255, 112, 72, 0.1);
                    padding: 12px 16px;
                }
            }
        }
    }
}

:deep(.account-detail-dialog) {
    .el-dialog {
        background: hsla(0, 0%, 100%, .1);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 112, 72, 0.3);
        border-radius: 16px;

        .el-dialog__header {
            border-bottom: 1px solid rgba(255, 112, 72, 0.2);

            .el-dialog__title {
                color: #e0e6ff;
                font-weight: 600;
            }

            .el-dialog__headerbtn {
                .el-dialog__close {
                    color: #e0e6ff;

                    &:hover {
                        color: #ff7048;
                    }
                }
            }
        }

        .el-dialog__body {
            color: #e0e6ff;
            padding: 20px;
        }

        .el-dialog__footer {
            border-top: 1px solid rgba(255, 112, 72, 0.2);
            padding: 16px 20px;

            .el-button {
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
                color: #e0e6ff;

                &:hover {
                    background: #ff7048;
                    border-color: #ff7048;
                }
            }
        }
    }
}

// 响应式设计
@media (max-width: 768px) {
    .account-pool-management {
        .operation-bar {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
        }

        .search-section {
            width: 100%;

            .el-input {
                width: 100%;
                margin-right: 0;
                margin-bottom: 12px;
            }
        }

        .stats-info {
            width: 100%;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
        }
    }
}
</style>